export interface DescriptorKey {
  name: string;
  start: number;
  end: number;
}

export interface DescriptorPart {
  text: string;
  key: string | null;
  moved: boolean;
}

const DELIMITER = /[\s()<>[\]{}/%]/;

const skipSpace = (s: string, i: number): number => {
  let at = i;
  while (at < s.length && /\s/.test(s[at])) at += 1;
  return at;
};

const tokenEnd = (s: string, i: number): number => {
  let at = i;
  while (at < s.length && !DELIMITER.test(s[at])) at += 1;
  return at;
};

const literalEnd = (s: string, i: number): number => {
  let depth = 0;
  for (let at = i; at < s.length; at += 1) {
    if (s[at] === '\\') at += 1;
    else if (s[at] === '(') depth += 1;
    else if (s[at] === ')') {
      depth -= 1;
      if (depth === 0) return at + 1;
    }
  }
  return -1;
};

const valueEnd = (s: string, i: number): number => {
  if (s.startsWith('<<', i)) return parseDict(s, i)?.end ?? -1;
  if (s[i] === '<') {
    const close = s.indexOf('>', i);
    return close === -1 ? -1 : close + 1;
  }
  if (s[i] === '(') return literalEnd(s, i);
  if (s[i] === '[') {
    let at = skipSpace(s, i + 1);
    while (at < s.length && s[at] !== ']') {
      const end = valueEnd(s, at);
      if (end === -1 || end === at) return -1;
      at = skipSpace(s, end);
    }
    return at < s.length ? at + 1 : -1;
  }
  if (s[i] === '/') return tokenEnd(s, i + 1);
  return tokenEnd(s, i);
};

function parseDict(s: string, start: number): { end: number; keys: DescriptorKey[] } | null {
  const keys: DescriptorKey[] = [];
  let at = skipSpace(s, start + 2);
  let afterKey = false;
  while (at < s.length) {
    if (s.startsWith('>>', at)) return { end: at + 2, keys };
    if (s[at] === '/' && !afterKey) {
      const end = tokenEnd(s, at + 1);
      keys.push({ name: s.slice(at + 1, end), start: at, end });
      afterKey = true;
      at = skipSpace(s, end);
    } else {
      const end = valueEnd(s, at);
      if (end === -1 || end <= at) return null;
      afterKey = false;
      at = skipSpace(s, end);
    }
  }
  return null;
}

export const descriptorKeys = (descriptor: string): DescriptorKey[] => {
  const start = descriptor.indexOf('<<');
  return start === -1 ? [] : parseDict(descriptor, start)?.keys ?? [];
};

export const descriptorParts = (descriptor: string, moved: string[]): DescriptorPart[] => {
  const parts: DescriptorPart[] = [];
  let at = 0;
  descriptorKeys(descriptor).forEach(({ name, start, end }) => {
    if (start > at) parts.push({ text: descriptor.slice(at, start), key: null, moved: false });
    parts.push({ text: descriptor.slice(start, end), key: name, moved: moved.includes(name) });
    at = end;
  });
  if (at < descriptor.length) parts.push({ text: descriptor.slice(at), key: null, moved: false });
  return parts;
};
